import React, { useRef, useEffect, useState } from 'react';
import { 
  AiOutlineMinus, AiOutlineShopping, 
  AiOutlinePlus, AiOutlineLeft, 
  AiOutlineSearch 
} from 'react-icons/ai';
import { TiDeleteOutline } from 'react-icons/ti';
import analytics from '../utils/analytics';
import { useStateContext } from '../context/StateContext';
import { urlFor } from '../sanity/sanityClient';
import {
  ordersubmit,
  getAllActiveAgents,
  clickpesaInitiate,
  clickpesaStatus,
  pesapalInitiate,
  pesapalStatus,
} from "../utils/APIRoutes";
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import PaymentOptionList from '../payment/PaymentOptionList';
import { PAYMENT_OPTIONS } from '../payment/paymentOptions';

const CartContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 500px;
  height: 100vh;
  background: white;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const CartTitle = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  margin-left: 1rem;
  flex-grow: 1;
`;

const CartItemCount = styled.span`
  background: #f5f5f5;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.875rem;
`;

const EmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  flex-grow: 1;
`;

const ProductContainer = styled.div`
  padding: 1rem;
  flex-grow: 1;
`;

const ProductItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
`;

const ItemDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductName = styled.h5`
  margin: 0;
  font-size: 1rem;
  color: #333;
`;

const ProductPrice = styled.span`
  color: #d32f2f;
  font-weight: bold;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;  
  cursor: pointer;
  margin-left: 0.5rem;
`;

const CartSummary = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #eee;
  background: white;
  position: sticky;
  bottom: 0;
`;

const Subtotal = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: #ff5a5f;
    color: white;
    
    &:hover {
      background: #e04a50;
    }
  }

  &.secondary {
    background: #f5f5f5;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckoutForm = styled.form`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: #0000FF;
`;

const FormInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff5a5f;
  }

  &:disabled {
    background: #f5f5f5;
    color: #666;
  }
`;

const AgentSearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0 1rem;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem 0.5rem;
  border: none;
  font-size: 1rem;
  
  &:focus {
    outline: none;
  }
`;

const AgentDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 10;
  margin-top: 0.5rem;
`;

const AgentOption = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  
  &:hover {
    background: #f9f9f9;
  }
`;

const AgentName = styled.div`
  font-weight: 600;
`;

const AgentLocation = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const SelectedAgent = styled.div`
  padding: 0.75rem;
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 0.5rem;
`;

const PaidBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 1px solid #b7e4c7;
  background: #eafaf1;
  border-radius: 8px;
  color: #1b7a43;
  font-weight: 600;
`;

const PaymentStatusBox = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${({ tone }) =>
    tone === 'error' ? '#fdecea' : tone === 'success' ? '#eafaf1' : '#fff8e6'};
  color: ${({ tone }) =>
    tone === 'error' ? '#c0392b' : tone === 'success' ? '#1b7a43' : '#8a6d1a'};
`;

const PaymentFrame = styled.iframe`
  width: 100%;
  height: 520px;
  border: 1px solid #eee;
  border-radius: 8px;
`;

// ~5 minutes of polling at 3s intervals before we offer a retry
const MAX_POLL_ATTEMPTS = 100;
const POLL_INTERVAL_MS = 3000;

// With a single enabled payment option there is nothing to choose, so skip the chooser
const SINGLE_METHOD = PAYMENT_OPTIONS.length === 1 ? PAYMENT_OPTIONS[0].id : null;

// Only accept the "payment finished" message from our own API origin
const API_ORIGIN = new URL(pesapalInitiate, window.location.href).origin;

const emptyForm = {
  fullName: '',
  address: '',
  email: '',
  phone: '',
  size: '',
  transactionId: '',
  agent: '',
};

const Cart = () => {
  const cartRef = useRef();
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const pollRef = useRef(null);
  const retryPaymentRef = useRef(null); // always points at the latest handleRetryPayment

  const {
    totalPrice,
    totalQuantities,
    cartItems,
    setShowCart,
    toggleCartItemQuantity,
    onRemove,
    clearCart,
    currencyWarning
  } = useStateContext();

  // 'cart' -> 'payment' (Pesapal iframe + polling) -> 'details' (rest of the form)
  const [stage, setStage] = useState('cart');
  const [formData, setFormData] = useState(emptyForm);

  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // --- Pesapal payment state ---
  const [method, setMethod] = useState(SINGLE_METHOD); // null (choosing) | 'clickpesa' | 'pesapal'
  const [payer, setPayer] = useState({ email: '', phone: '' });
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | sending | awaiting | failed | timeout
  const [paymentError, setPaymentError] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  // The gateway's payment reference: Pesapal's orderTrackingId or ClickPesa's orderReference
  const [orderTrackingId, setOrderTrackingId] = useState(null);
  const [confirmationCode, setConfirmationCode] = useState(null);

  useEffect(() => {
    if (stage !== 'cart' && cartRef.current) {
      cartRef.current.scrollTo({
        top: cartRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [stage]);

  useEffect(() => {
    if (stage === 'details') fetchAgents();
  }, [stage]);

  useEffect(() => {
    return () => clearPollTimer();
  }, []);

  // The iframe's callback page tells us "payment submitted" -> check right away
  // instead of waiting for the next 3s tick. The poll is still what decides success.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== API_ORIGIN) return;
      if (event.data?.type === 'pesapal-callback' && pollRef.current) {
        pollRef.current();
      }
      if (event.data?.type === 'pesapal-cancelled') {
        retryPaymentRef.current?.(); // shopper hit Cancel on Pesapal's page
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await fetch(getAllActiveAgents);
      const data = await response.json();
      setAgents(data);
      setFilteredAgents(data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');

      analytics.trackError(error, {
        context: 'fetch_agents',
        location: 'Cart Component',
        step: 'agent_loading'
      });
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    setStage('payment');
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const stopPolling = () => {
    clearPollTimer();
    pollAttemptsRef.current = 0;
    pollRef.current = null;
  };

  const pollPaymentStatus = async (provider, reference) => {
    clearPollTimer();
    try {
      const statusUrl = provider === 'clickpesa' ? clickpesaStatus : pesapalStatus;
      const response = await fetch(`${statusUrl}/${reference}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not check payment status.');
      }

      if (data.status === 'COMPLETED') {
        stopPolling();
        setConfirmationCode(data.confirmationCode || 'PAID');
        setRedirectUrl(null);
        setFormData((prev) => ({
          ...prev,
          email: prev.email || payer.email,
          phone: prev.phone || payer.phone,
          transactionId: data.confirmationCode || 'PAID',
        }));
        setStage('details');
        return;
      }

      const TERMINAL_MESSAGES = {
        FAILED: 'The payment did not go through. Please try again.',
        REVERSED: 'This payment was reversed. If money left your account, it will be returned to you.',
        ON_HOLD: 'Your payment is on hold. Please contact support with your phone number.',
        MISMATCH: "We couldn't match this payment to your order. Please contact support.",
      };
      if (TERMINAL_MESSAGES[data.status]) {
        stopPolling();
        setRedirectUrl(null);
        setPaymentStatus('failed');
        setPaymentError(TERMINAL_MESSAGES[data.status]);
        return;
      }

      // PENDING / INVALID (not paid yet) — keep waiting, up to the limit
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        stopPolling();
        setRedirectUrl(null);
        setPaymentStatus('timeout');
        return;
      }
      pollTimerRef.current = setTimeout(() => pollPaymentStatus(provider, reference), POLL_INTERVAL_MS);
    } catch (error) {
      stopPolling();
      setRedirectUrl(null);
      setPaymentStatus('failed');
      setPaymentError(error.message);
      analytics.trackError(error, {
        context: `${provider}_poll_status`,
        location: 'Cart Component',
        paymentReference: reference,
      });
    }
  };

  const handleStartPesapal = async (e) => {
    e.preventDefault();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payer.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (payer.phone.replace(/\D/g, '').length < 9) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setPaymentStatus('sending');
    setPaymentError(null);

    try {
      const response = await fetch(pesapalInitiate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalPrice,
          currency: cartItems[0]?.currency || 'TZS',
          email: payer.email,
          phone: payer.phone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not start the payment.');
      }

      setOrderTrackingId(data.orderTrackingId);
      setRedirectUrl(data.redirectUrl);
      setPaymentStatus('awaiting');

      pollAttemptsRef.current = 0;
      pollRef.current = () => pollPaymentStatus('pesapal', data.orderTrackingId);
      pollTimerRef.current = setTimeout(pollRef.current, POLL_INTERVAL_MS);
    } catch (error) {
      setPaymentStatus('failed');
      setPaymentError(error.message);
      toast.error(error.message);
      analytics.trackError(error, {
        context: 'pesapal_initiate',
        location: 'Cart Component',
      });
    }
  };

  const handleStartClickPesa = async (e) => {
    e.preventDefault();

    const cleanPhone = payer.phone.replace(/\D/g, '');
    if (!/^\d{9,12}$/.test(cleanPhone)) {
      toast.error('Enter a valid mobile money number');
      return;
    }

    setPaymentStatus('sending');
    setPaymentError(null);

    try {
      const response = await fetch(clickpesaInitiate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: cleanPhone, amount: totalPrice }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not start the payment prompt.');
      }

      setOrderTrackingId(data.orderReference);
      setPaymentStatus('awaiting');

      pollAttemptsRef.current = 0;
      pollRef.current = () => pollPaymentStatus('clickpesa', data.orderReference);
      pollTimerRef.current = setTimeout(pollRef.current, POLL_INTERVAL_MS);
    } catch (error) {
      setPaymentStatus('failed');
      setPaymentError(error.message);
      toast.error(error.message);
      analytics.trackError(error, {
        context: 'clickpesa_initiate',
        location: 'Cart Component',
      });
    }
  };

  const handleRetryPayment = () => {
    stopPolling();
    setPaymentStatus('idle');
    setPaymentError(null);
    setRedirectUrl(null);
    setOrderTrackingId(null);
    setMethod(SINGLE_METHOD); // back to the method chooser (or the only method)
  };
  retryPaymentRef.current = handleRetryPayment;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAgentSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFilteredAgents(
      term === ''
        ? agents
        : agents.filter(agent =>
            agent.fullName.toLowerCase().includes(term) ||
            agent.location.toLowerCase().includes(term)
          )
    );
  };

  const selectAgent = (agent) => {
    setFormData(prev => ({ ...prev, agent: agent }));
    setShowAgentDropdown(false);
    setSearchTerm(agent.fullName);
  };

  const validateForm = () => {
    const requiredFields = ['fullName', 'address', 'email', 'phone', 'transactionId'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    const phoneRegex = /^\d{10,}$/;
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      toast.error('Please enter a valid phone number (at least 10 digits)');
      return false;
    }

    if (!orderTrackingId) {
      toast.error('Payment must complete before you can submit the order');
      return false;
    }

    if (!formData.agent || !formData.agent._id) {
      toast.error('Please select an agent');
      return false;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return false;
    }

    return true;
  };

  const resetPaymentFlow = () => {
    stopPolling();
    setPaymentStatus('idle');
    setPaymentError(null);
    setRedirectUrl(null);
    setOrderTrackingId(null);
    setConfirmationCode(null);
    setMethod(SINGLE_METHOD);
    setPayer({ email: '', phone: '' });
    setSearchTerm('');
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const response = await fetch(ordersubmit, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The server re-checks this payment with the gateway before saving the order
        body: JSON.stringify({
          ...formData,
          cartItems,
          paymentProvider: method,
          paymentReference: orderTrackingId,
        }),
      });

      if (response.ok) {
        const orderData = await response.json();

        toast.success("Order submitted successfully!");

        analytics.trackPurchase({
          _id: orderData._id || `temp_${Date.now()}`,
          total: totalPrice,
          currency: cartItems[0]?.currency || "TZS",
          items: cartItems.map((item) => ({
            productId: item._id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            currency: item.currency,
            location: item.location,
          })),
        });

        clearCart();
        resetPaymentFlow();
        setStage('cart');
        setShowCart(false);
      } else {
        throw new Error(`Failed to submit order: HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(error);

      toast.error("Failed to submit order. Please try again.");

      analytics.trackError(error, {
        context: "order_submission",
        location: "Cart Component",
        step: "order_processing",
        cartItemCount: cartItems.length,
        totalPrice,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuantityIncrease = (item) => {
    analytics.trackAddToCart(item, 1);
    toggleCartItemQuantity(item._id, 'inc');
  };

  const handleQuantityDecrease = (item) => {
    toggleCartItemQuantity(item._id, 'dec');
  };

  const handleRemoveItem = (item) => {
    analytics.trackRemoveFromCart(item, item.quantity);
    onRemove(item);
  };

  const currentCurrency = cartItems[0]?.currency || 'TZS';

  return (
    <CartContainer ref={cartRef}>
      <CartHeader>
        <AiOutlineLeft
          onClick={() => {
            if (stage === 'cart') {
              setShowCart(false);
            } else if (stage === 'payment') {
              // Back out of an unfinished payment without losing the cart
              handleRetryPayment();
              setStage('cart');
            }
            // Once paid (details stage) we stay put so the payment isn't orphaned
          }}
          style={{ cursor: 'pointer' }}
        />
        <CartTitle>Your Cart</CartTitle>
        <CartItemCount>{totalQuantities} items</CartItemCount>
      </CartHeader>

      {currencyWarning && (
        <div style={{ color: 'red', padding: '0 1.5rem', marginBottom: '1rem' }}>
          {currencyWarning}
        </div>
      )}

      {cartItems.length < 1 ? (
        <EmptyCart>
          <AiOutlineShopping size={100} color="#ccc" />
          <h3>Your shopping bag is empty</h3>
          <Button
            className="secondary"
            onClick={() => setShowCart(false)}
            style={{ marginTop: '1rem' }}
          >
            Continue Shopping
          </Button>
        </EmptyCart>
      ) : (
        <>
          <ProductContainer>
            {cartItems.map((item) => (
              <ProductItem key={`${item._id}-${item.selectedIndex}`}>
                <ProductImage
                  src={urlFor(item?.image[0])}
                  alt={item.name}
                />
                <ItemDetails>
                  <div>
                    <ProductName>
                      {item.name}
                    </ProductName>
                    <ProductPrice>
                      {item.currency || 'TZS'}{item.price.toLocaleString()}/=
                    </ProductPrice>
                  </div>
                  <QuantityControls>
                    <QuantityButton onClick={() => handleQuantityDecrease(item)}>
                      <AiOutlineMinus />
                    </QuantityButton>
                    <span>{item.quantity}</span>
                    <QuantityButton onClick={() => handleQuantityIncrease(item)}>
                      <AiOutlinePlus />
                    </QuantityButton>
                    <RemoveButton onClick={() => handleRemoveItem(item)}>
                      <TiDeleteOutline size={20} />
                    </RemoveButton>
                    <span style={{fontSize:'11px'}}>{item.location}</span>
                  </QuantityControls>
                </ItemDetails>
              </ProductItem>
            ))}
          </ProductContainer>

          {stage === 'cart' && (
            <CartSummary>
              <Subtotal>SubTotal: {currentCurrency}{totalPrice.toLocaleString()}/=</Subtotal>
              <ButtonContainer>
                <Button className="primary" onClick={handleCheckout}>
                  Pay Now
                </Button>
                <Button className="secondary" onClick={clearCart}>
                  Clear Cart
                </Button>
              </ButtonContainer>
            </CartSummary>
          )}

          {stage === 'payment' && (
            <CheckoutForm onSubmit={method === 'clickpesa' ? handleStartClickPesa : handleStartPesapal}>
              <Subtotal>SubTotal: {currentCurrency}{totalPrice.toLocaleString()}/=</Subtotal>

              {paymentStatus === 'awaiting' ? (
                <>
                  {method === 'pesapal' && redirectUrl ? (
                    <>
                      <FormTitle>Complete your payment below</FormTitle>
                      <PaymentFrame src={redirectUrl} title="Pesapal checkout" />
                    </>
                  ) : (
                    <FormTitle>Approve the payment on your phone</FormTitle>
                  )}
                  <PaymentStatusBox tone="pending">
                    {method === 'clickpesa' &&
                      `Enter your PIN on the prompt to pay ${currentCurrency}${totalPrice.toLocaleString()}/=. `}
                    Waiting for confirmation — this moves on automatically once you've paid.
                  </PaymentStatusBox>
                  <Button type="button" className="secondary" onClick={handleRetryPayment}>
                    Cancel payment
                  </Button>
                </>
              ) : !method ? (
                <>
                  <FormTitle>Choose how to pay</FormTitle>
                  <PaymentOptionList
                    options={PAYMENT_OPTIONS.map((option) =>
                      // ClickPesa's USSD push is TZS-only
                      option.id === 'clickpesa' && currentCurrency !== 'TZS'
                        ? { ...option, unavailableReason: 'Available for TZS orders only' }
                        : option
                    )}
                    value={method}
                    onChange={setMethod}
                  />
                </>
              ) : (
                <>
                  <FormTitle>
                    {method === 'clickpesa'
                      ? 'Pay with a mobile money prompt'
                      : 'Pay securely with Pesapal'}
                  </FormTitle>

                  {method === 'clickpesa' && currentCurrency !== 'TZS' && (
                    <PaymentStatusBox tone="error">
                      Mobile money prompts only support TZS orders. This cart is in {currentCurrency}.
                    </PaymentStatusBox>
                  )}

                  {method === 'pesapal' && (
                    <FormInput
                      type="email"
                      placeholder="Email for your receipt"
                      value={payer.email}
                      onChange={(e) => setPayer((p) => ({ ...p, email: e.target.value }))}
                      disabled={paymentStatus === 'sending'}
                      required
                    />
                  )}
                  <FormInput
                    type="tel"
                    placeholder={method === 'clickpesa' ? 'Mobile money number, e.g. 0712345678' : 'Phone number, e.g. 0712345678'}
                    value={payer.phone}
                    onChange={(e) => setPayer((p) => ({ ...p, phone: e.target.value }))}
                    disabled={paymentStatus === 'sending'}
                    required
                  />

                  {paymentStatus === 'failed' && (
                    <PaymentStatusBox tone="error">
                      {paymentError || 'Payment failed.'}
                    </PaymentStatusBox>
                  )}

                  {paymentStatus === 'timeout' && (
                    <PaymentStatusBox tone="error">
                      We didn't get a confirmation in time. If money left your account, don't
                      pay again — contact support.
                    </PaymentStatusBox>
                  )}

                  <ButtonContainer>
                    <Button
                      type="submit"
                      className="primary"
                      disabled={paymentStatus === 'sending' || (method === 'clickpesa' && currentCurrency !== 'TZS')}
                    >
                      {paymentStatus === 'sending'
                        ? 'Starting payment…'
                        : method === 'clickpesa'
                        ? 'Send Payment Prompt'
                        : 'Continue to Payment'}
                    </Button>
                    {(!SINGLE_METHOD || paymentStatus === 'failed' || paymentStatus === 'timeout') && (
                      <Button type="button" className="secondary" onClick={handleRetryPayment}>
                        {paymentStatus === 'failed' || paymentStatus === 'timeout'
                          ? 'Try again'
                          : 'Change method'}
                      </Button>
                    )}
                  </ButtonContainer>
                </>
              )}
            </CheckoutForm>
          )}

          {stage === 'details' && (
            <CheckoutForm onSubmit={handleSubmit}>
              <Subtotal>SubTotal: {currentCurrency}{totalPrice.toLocaleString()}/=</Subtotal>

              <PaidBadge>
                <span>Paid via {method === 'clickpesa' ? 'ClickPesa' : 'Pesapal'}</span>
                <span>{confirmationCode}</span>
              </PaidBadge>

              {['fullName', 'address', 'email', 'phone', 'size'].map((field) => (
                <FormInput
                  key={field}
                  type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  name={field}
                  placeholder={
                    field === 'size' ? 'Enter size/color (optional)' :
                    field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')
                  }
                  value={formData[field]}
                  onChange={handleInputChange}
                  required={field !== 'size'}
                />
              ))}

              {/* Filled automatically from Pesapal's confirmation code — no manual paste */}
              <FormInput
                type="text"
                name="transactionId"
                value={formData.transactionId}
                disabled
              />

              <div style={{ position: 'relative' }}>
                <AgentSearchContainer>
                  <AiOutlineSearch />
                  <SearchInput
                    type="text"
                    placeholder="Search agent by name or location"
                    value={searchTerm}
                    onChange={handleAgentSearch}
                    onFocus={() => setShowAgentDropdown(true)}
                    required
                  />
                </AgentSearchContainer>

                {showAgentDropdown && (
                  <AgentDropdown>
                    {loadingAgents ? (
                      <div style={{ padding: '1rem', textAlign: 'center' }}>Loading agents...</div>
                    ) : filteredAgents.length > 0 ? (
                      filteredAgents.map(agent => (
                        <AgentOption
                          key={agent._id}
                          onClick={() => selectAgent(agent)}
                        >
                          <AgentName>{agent.fullName}</AgentName>
                          <AgentLocation>{agent.location}</AgentLocation>
                        </AgentOption>
                      ))
                    ) : (
                      <div style={{ padding: '1rem', textAlign: 'center' }}>No agents found</div>
                    )}
                  </AgentDropdown>
                )}

                {formData.agent && (
                  <SelectedAgent>
                    Selected: {formData.agent?.fullName}
                  </SelectedAgent>
                )}
              </div>

              <Button
                type="submit"
                className="primary"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Order"}
              </Button>
            </CheckoutForm>
          )}
        </>
      )}
    </CartContainer>
  );
};

export default Cart;
